
import { SingleHit } from "../services/help_articles.service"

export interface SearchableHit extends SingleHit {
  relevanceScore?: number
}

interface RelevanceConfig {
  exactPhraseBonus: {
    title: number
    summary: number
  }
  wordMatchBonus: {
    title: {
      base: number
      positionAtStart: number
      positionNearStart: number
      positionElsewhere: number
    }
    summary: number
    category: number
  }
  partialMatchBonus: {
    title: number
    summary: number
  }
  multiWordBonus: number
  shortTitleBonus: {
    threshold: number
    points: number
  }
  categorySpecificRules?: {
    keywords: string[]
    categories: string[]
    bonus: number
  }[]
}

const DEFAULT_RELEVANCE_CONFIG: RelevanceConfig = {
  exactPhraseBonus: {
    title: 1000,
    summary: 500
  },
  wordMatchBonus: {
    title: {
      base: 100,
      positionAtStart: 200,
      positionNearStart: 100,
      positionElsewhere: 50
    },
    summary: 50,
    category: 75
  },
  partialMatchBonus: {
    title: 25,
    summary: 15
  },
  multiWordBonus: 30,
  shortTitleBonus: {
    threshold: 50,
    points: 20
  },
  categorySpecificRules: [
    {
      keywords: ['bank', 'payment'],
      categories: ['payment', 'financial', 'organizer'],
      bonus: 50
    }
  ]
}


export const calculateRelevanceScore = (
  hit: SingleHit,
  query: string,
  config: RelevanceConfig = DEFAULT_RELEVANCE_CONFIG
): number => {
  if (!hit?.fields?.helparticletitle || !query) return 0

  const title = hit.fields.helparticletitle.toLowerCase()
  const summary = hit.fields.summary?.toLowerCase() || ''
  const category = hit.fields.helparticlecategory?.toLowerCase() || ''
  const searchQuery = query.toLowerCase().trim()
  
  let score = 0
  const queryWords = searchQuery.split(/\s+/).filter(word => word.length > 1)

  // 1. EXACT PHRASE MATCHING (highest priority)
  if (title.includes(searchQuery)) {
    score += config.exactPhraseBonus.title
  }
  if (summary.includes(searchQuery)) {
    score += config.exactPhraseBonus.summary
  }

  // 2. INDIVIDUAL WORD MATCHING with position weighting
  queryWords.forEach((word, index) => {
    const wordWeight = queryWords.length - index
    
    if (title.includes(word)) {
      const titlePosition = title.indexOf(word)
      const positionBonus = 
        titlePosition === 0 ? config.wordMatchBonus.title.positionAtStart :
        titlePosition < 10 ? config.wordMatchBonus.title.positionNearStart :
        config.wordMatchBonus.title.positionElsewhere
      
      score += (config.wordMatchBonus.title.base + positionBonus) * wordWeight
    }
    
    if (summary.includes(word)) {
      score += config.wordMatchBonus.summary * wordWeight
    }
    
    if (category.includes(word)) {
      score += config.wordMatchBonus.category * wordWeight
    }
  })

  // 3. PARTIAL WORD MATCHING (for typos and variations)
  queryWords.forEach(word => {
    if (word.length > 3) {
      const titleWords = title.split(/\s+/)
      const summaryWords = summary.split(/\s+/)
      
      titleWords.forEach(titleWord => {
        if (titleWord.includes(word) || word.includes(titleWord)) {
          score += config.partialMatchBonus.title
        }
      })
      
      summaryWords.forEach(summaryWord => {
        if (summaryWord.includes(word) || word.includes(summaryWord)) {
          score += config.partialMatchBonus.summary
        }
      })
    }
  })

  // 4. BONUS SCORING
  const wordsFound = queryWords.filter(word => 
    title.includes(word) || summary.includes(word)
  ).length
  
  if (wordsFound > 1) {
    score += wordsFound * config.multiWordBonus
  }

  if (title.length < config.shortTitleBonus.threshold && score > 0) {
    score += config.shortTitleBonus.points
  }

  // 5. CATEGORY-SPECIFIC BONUSES
  if (config.categorySpecificRules) {
    config.categorySpecificRules.forEach(rule => {
      const queryMatchesKeywords = rule.keywords.some(keyword => 
        searchQuery.includes(keyword)
      )
      const categoryMatchesRule = rule.categories.some(cat => 
        category.includes(cat)
      )
      
      if (queryMatchesKeywords && categoryMatchesRule) {
        score += rule.bonus
      }
    })
  }

  return Math.max(0, score)
}

export const processSearchResultsWithRelevance = (
  hits: SingleHit[],
  query: string,
  config?: RelevanceConfig
): SearchableHit[] => {
  if (!hits?.length || !query) return []

  const scoredHits = hits.map(hit => ({
    ...hit,
    relevanceScore: calculateRelevanceScore(hit, query, config)
  }))

  const deduplicatedHits = deduplicateHitsByScore(scoredHits)

  // Sort by relevance score (highest first) and filter out zero scores
  const sortedHits = Array.from(deduplicatedHits.values())
    .filter(hit => (hit.relevanceScore || 0) > 0)
    .sort((a, b) => (b.relevanceScore || 0) - (a.relevanceScore || 0))

  return sortedHits
}


export const deduplicateHitsByScore = (
  hits: SearchableHit[]
): Map<string, SearchableHit> => {
  const deduplicatedHits = new Map<string, SearchableHit>()
  
  hits.forEach(hit => {
    const articleId = hit?.fields?.articleid
    if (!articleId) return
    
    const existing = deduplicatedHits.get(articleId)
    if (!existing || (hit.relevanceScore || 0) > (existing.relevanceScore || 0)) {
      deduplicatedHits.set(articleId, hit)
    }
  })

  return deduplicatedHits
}